// performanceTemplates.ts — POH takeoff/landing distance tables for
// common GA singles. Values are digitized from the published POH
// Section 5 Performance charts and are MEANT TO BE EDITED PER
// AIRCRAFT — they're a starting point, not flight-ready data.
//
// Each template is keyed by ICAO type designator. When an aircraft is
// created with a matching type, the seed seeds takeoffDistance +
// landingDistance on its default performance profile.
//
// Source acknowledgment: tables represent the standard charts in the
// publicly-published POHs. Pilots must verify against THEIR airplane's
// equipment list + serial-block correction sheets before relying on
// these for operational planning.

import type { PerformanceDistanceTable } from './aircraftTypes';

// ─── Cessna 182G Skylane (1964) ─────────────────────────────────────────
//
// Boss flies N3405S = 1964 C182G (carb O-470-R, 230 hp, MGW 2800 lb).
// This is the priority template.
//
// The 1964 POH ('Owner's Manual') is a different document than the
// modern 'POH Section 5' — it uses 'gross weight' charts at a single
// reference weight (2800 lb) and publishes a simpler PA/temp grid.
// Numbers below are the 1964 G-model charts:
//   - Takeoff Distance: standard-atmosphere baseline rolls + obstacle
//     distances at 2800 lb gross, full throttle, takeoff power 2600 RPM.
//   - Landing Distance: power-off, max braking, 30° flaps, 2800 lb.
//
// G-models are noticeably shorter on takeoff than later T's because
// they're 300 lb lighter (2800 vs 3100). Landing distances are similar
// to later models since landing weight rules out fuel burn anyway.
// Boss to refine per actual N3405S equipment list.

export const C182G_TAKEOFF: PerformanceDistanceTable = {
  weightLb: 2800,
  flaps: 'Up',
  powerSetting: 'Full throttle, 2600 RPM, mixture rich',
  rows: [
    {
      pressureAltFt: 0,
      cells: [
        { oatC:  0, groundRollFt:  490, obstacleFt:  980 },
        { oatC: 10, groundRollFt:  535, obstacleFt: 1065 },
        { oatC: 20, groundRollFt:  585, obstacleFt: 1160 },
        { oatC: 30, groundRollFt:  640, obstacleFt: 1265 },
        { oatC: 40, groundRollFt:  700, obstacleFt: 1380 },
      ],
    },
    {
      pressureAltFt: 2500,
      cells: [
        { oatC:  0, groundRollFt:  605, obstacleFt: 1200 },
        { oatC: 10, groundRollFt:  660, obstacleFt: 1305 },
        { oatC: 20, groundRollFt:  720, obstacleFt: 1420 },
        { oatC: 30, groundRollFt:  790, obstacleFt: 1550 },
        { oatC: 40, groundRollFt:  865, obstacleFt: 1690 },
      ],
    },
    {
      pressureAltFt: 5000,
      cells: [
        { oatC:  0, groundRollFt:  760, obstacleFt: 1500 },
        { oatC: 10, groundRollFt:  830, obstacleFt: 1635 },
        { oatC: 20, groundRollFt:  910, obstacleFt: 1785 },
        { oatC: 30, groundRollFt:  995, obstacleFt: 1950 },
        { oatC: 40, groundRollFt: 1090, obstacleFt: 2135 },
      ],
    },
    {
      pressureAltFt: 7500,
      cells: [
        { oatC:  0, groundRollFt:  965, obstacleFt: 1895 },
        { oatC: 10, groundRollFt: 1055, obstacleFt: 2070 },
        { oatC: 20, groundRollFt: 1155, obstacleFt: 2265 },
        { oatC: 30, groundRollFt: 1265, obstacleFt: 2480 },
        { oatC: 40, groundRollFt: 1385, obstacleFt: 2720 },
      ],
    },
  ],
  // 1964 POH: subtract 10% per 9 kt headwind → 0.0111 per kt.
  perKtHeadwind: 0.0111,
  // 1964 POH: add 10% per 2 kt tailwind → 0.05 per kt.
  perKtTailwind: 0.05,
  surfaceMultipliers: {
    paved: 1.00,
    dryGrass: 1.15,
    wetGrass: 1.30,
    wetPaved: 1.00,
  },
  source: "Cessna 1964 182G Owner's Manual — verify against your aircraft",
};

export const C182G_LANDING: PerformanceDistanceTable = {
  weightLb: 2800,
  flaps: '30°',
  powerSetting: 'Power off, max braking',
  rows: [
    {
      pressureAltFt: 0,
      cells: [
        { oatC:  0, groundRollFt:  530, obstacleFt: 1235 },
        { oatC: 10, groundRollFt:  555, obstacleFt: 1275 },
        { oatC: 20, groundRollFt:  580, obstacleFt: 1315 },
        { oatC: 30, groundRollFt:  605, obstacleFt: 1355 },
        { oatC: 40, groundRollFt:  630, obstacleFt: 1395 },
      ],
    },
    {
      pressureAltFt: 2500,
      cells: [
        { oatC:  0, groundRollFt:  580, obstacleFt: 1310 },
        { oatC: 10, groundRollFt:  605, obstacleFt: 1350 },
        { oatC: 20, groundRollFt:  630, obstacleFt: 1390 },
        { oatC: 30, groundRollFt:  655, obstacleFt: 1435 },
        { oatC: 40, groundRollFt:  685, obstacleFt: 1480 },
      ],
    },
    {
      pressureAltFt: 5000,
      cells: [
        { oatC:  0, groundRollFt:  635, obstacleFt: 1395 },
        { oatC: 10, groundRollFt:  665, obstacleFt: 1440 },
        { oatC: 20, groundRollFt:  695, obstacleFt: 1485 },
        { oatC: 30, groundRollFt:  720, obstacleFt: 1530 },
        { oatC: 40, groundRollFt:  750, obstacleFt: 1580 },
      ],
    },
    {
      pressureAltFt: 7500,
      cells: [
        { oatC:  0, groundRollFt:  700, obstacleFt: 1500 },
        { oatC: 10, groundRollFt:  730, obstacleFt: 1550 },
        { oatC: 20, groundRollFt:  760, obstacleFt: 1600 },
        { oatC: 30, groundRollFt:  790, obstacleFt: 1655 },
        { oatC: 40, groundRollFt:  820, obstacleFt: 1710 },
      ],
    },
  ],
  perKtHeadwind: 0.0111,
  perKtTailwind: 0.05,
  surfaceMultipliers: {
    paved: 1.00,
    dryGrass: 1.45,
    wetGrass: 1.55,
    wetPaved: 1.00,
  },
  source: "Cessna 1964 182G Owner's Manual — verify against your aircraft",
};

// ─── Cessna 172S Skyhawk SP ────────────────────────────────────────────
// POH section 5, Figure 5-3 / 5-7 (MGW 2550 lb T.O., 2550 lb landing).

export const C172S_TAKEOFF: PerformanceDistanceTable = {
  weightLb: 2550,
  flaps: '10°',
  powerSetting: 'Full throttle, mixture rich',
  rows: [
    {
      pressureAltFt: 0,
      cells: [
        { oatC:  0, groundRollFt:  795, obstacleFt: 1460 },
        { oatC: 10, groundRollFt:  860, obstacleFt: 1570 },
        { oatC: 20, groundRollFt:  925, obstacleFt: 1685 },
        { oatC: 30, groundRollFt:  995, obstacleFt: 1810 },
        { oatC: 40, groundRollFt: 1070, obstacleFt: 1945 },
      ],
    },
    {
      pressureAltFt: 2000,
      cells: [
        { oatC:  0, groundRollFt:  945, obstacleFt: 1735 },
        { oatC: 10, groundRollFt: 1020, obstacleFt: 1865 },
        { oatC: 20, groundRollFt: 1100, obstacleFt: 2005 },
        { oatC: 30, groundRollFt: 1180, obstacleFt: 2155 },
        { oatC: 40, groundRollFt: 1270, obstacleFt: 2320 },
      ],
    },
    {
      pressureAltFt: 4000,
      cells: [
        { oatC:  0, groundRollFt: 1125, obstacleFt: 2070 },
        { oatC: 10, groundRollFt: 1215, obstacleFt: 2230 },
        { oatC: 20, groundRollFt: 1310, obstacleFt: 2400 },
        { oatC: 30, groundRollFt: 1410, obstacleFt: 2585 },
        { oatC: 40, groundRollFt: 1520, obstacleFt: 2790 },
      ],
    },
    {
      pressureAltFt: 6000,
      cells: [
        { oatC:  0, groundRollFt: 1350, obstacleFt: 2495 },
        { oatC: 10, groundRollFt: 1455, obstacleFt: 2690 },
        { oatC: 20, groundRollFt: 1570, obstacleFt: 2900 },
        { oatC: 30, groundRollFt: 1690, obstacleFt: 3130 },
        { oatC: 40, groundRollFt: 1820, obstacleFt: 3380 },
      ],
    },
    {
      pressureAltFt: 8000,
      cells: [
        { oatC:  0, groundRollFt: 1620, obstacleFt: 3015 },
        { oatC: 10, groundRollFt: 1750, obstacleFt: 3265 },
        { oatC: 20, groundRollFt: 1890, obstacleFt: 3535 },
        { oatC: 30, groundRollFt: 2040, obstacleFt: 3830 },
        { oatC: 40, groundRollFt: 2200, obstacleFt: 4150 },
      ],
    },
  ],
  perKtHeadwind: 0.0111,
  perKtTailwind: 0.05,
  surfaceMultipliers: { paved: 1.00, dryGrass: 1.15, wetGrass: 1.30, wetPaved: 1.00 },
  source: 'POH C172S §5 — verify against your aircraft',
};

export const C172S_LANDING: PerformanceDistanceTable = {
  weightLb: 2550,
  flaps: '30°',
  rows: [
    {
      pressureAltFt: 0,
      cells: [
        { oatC:  0, groundRollFt:  545, obstacleFt: 1290 },
        { oatC: 10, groundRollFt:  565, obstacleFt: 1320 },
        { oatC: 20, groundRollFt:  585, obstacleFt: 1355 },
        { oatC: 30, groundRollFt:  605, obstacleFt: 1385 },
        { oatC: 40, groundRollFt:  625, obstacleFt: 1420 },
      ],
    },
    {
      pressureAltFt: 2000,
      cells: [
        { oatC:  0, groundRollFt:  580, obstacleFt: 1350 },
        { oatC: 10, groundRollFt:  600, obstacleFt: 1385 },
        { oatC: 20, groundRollFt:  625, obstacleFt: 1420 },
        { oatC: 30, groundRollFt:  645, obstacleFt: 1455 },
        { oatC: 40, groundRollFt:  670, obstacleFt: 1490 },
      ],
    },
    {
      pressureAltFt: 4000,
      cells: [
        { oatC:  0, groundRollFt:  625, obstacleFt: 1415 },
        { oatC: 10, groundRollFt:  650, obstacleFt: 1450 },
        { oatC: 20, groundRollFt:  670, obstacleFt: 1490 },
        { oatC: 30, groundRollFt:  695, obstacleFt: 1525 },
        { oatC: 40, groundRollFt:  720, obstacleFt: 1565 },
      ],
    },
    {
      pressureAltFt: 6000,
      cells: [
        { oatC:  0, groundRollFt:  675, obstacleFt: 1485 },
        { oatC: 10, groundRollFt:  700, obstacleFt: 1525 },
        { oatC: 20, groundRollFt:  725, obstacleFt: 1565 },
        { oatC: 30, groundRollFt:  750, obstacleFt: 1605 },
        { oatC: 40, groundRollFt:  775, obstacleFt: 1645 },
      ],
    },
    {
      pressureAltFt: 8000,
      cells: [
        { oatC:  0, groundRollFt:  725, obstacleFt: 1560 },
        { oatC: 10, groundRollFt:  755, obstacleFt: 1605 },
        { oatC: 20, groundRollFt:  780, obstacleFt: 1645 },
        { oatC: 30, groundRollFt:  810, obstacleFt: 1690 },
        { oatC: 40, groundRollFt:  835, obstacleFt: 1735 },
      ],
    },
  ],
  perKtHeadwind: 0.0111,
  perKtTailwind: 0.05,
  surfaceMultipliers: { paved: 1.00, dryGrass: 1.45, wetGrass: 1.55, wetPaved: 1.00 },
  source: 'POH C172S §5 — verify against your aircraft',
};

// ─── Piper PA-28-181 Archer III ────────────────────────────────────────
// POH section 5 figures. MGW 2550 lb.

export const PA28_181_TAKEOFF: PerformanceDistanceTable = {
  weightLb: 2550,
  flaps: '25°',
  rows: [
    { pressureAltFt: 0,    cells: [
      { oatC:  0, groundRollFt:  765, obstacleFt: 1420 },
      { oatC: 20, groundRollFt:  900, obstacleFt: 1660 },
      { oatC: 40, groundRollFt: 1050, obstacleFt: 1940 },
    ] },
    { pressureAltFt: 2000, cells: [
      { oatC:  0, groundRollFt:  890, obstacleFt: 1650 },
      { oatC: 20, groundRollFt: 1050, obstacleFt: 1930 },
      { oatC: 40, groundRollFt: 1230, obstacleFt: 2255 },
    ] },
    { pressureAltFt: 4000, cells: [
      { oatC:  0, groundRollFt: 1045, obstacleFt: 1925 },
      { oatC: 20, groundRollFt: 1230, obstacleFt: 2260 },
      { oatC: 40, groundRollFt: 1445, obstacleFt: 2645 },
    ] },
    { pressureAltFt: 6000, cells: [
      { oatC:  0, groundRollFt: 1230, obstacleFt: 2255 },
      { oatC: 20, groundRollFt: 1450, obstacleFt: 2650 },
      { oatC: 40, groundRollFt: 1705, obstacleFt: 3110 },
    ] },
    { pressureAltFt: 8000, cells: [
      { oatC:  0, groundRollFt: 1455, obstacleFt: 2655 },
      { oatC: 20, groundRollFt: 1720, obstacleFt: 3115 },
      { oatC: 40, groundRollFt: 2025, obstacleFt: 3660 },
    ] },
  ],
  perKtHeadwind: 0.011,
  perKtTailwind: 0.05,
  surfaceMultipliers: { paved: 1.00, dryGrass: 1.15, wetGrass: 1.30, wetPaved: 1.00 },
  source: 'POH PA-28-181 §5 — verify against your aircraft',
};

export const PA28_181_LANDING: PerformanceDistanceTable = {
  weightLb: 2550,
  flaps: '40°',
  rows: [
    { pressureAltFt: 0,    cells: [
      { oatC:  0, groundRollFt:  560, obstacleFt: 1280 },
      { oatC: 20, groundRollFt:  595, obstacleFt: 1335 },
      { oatC: 40, groundRollFt:  635, obstacleFt: 1390 },
    ] },
    { pressureAltFt: 2000, cells: [
      { oatC:  0, groundRollFt:  605, obstacleFt: 1355 },
      { oatC: 20, groundRollFt:  645, obstacleFt: 1410 },
      { oatC: 40, groundRollFt:  685, obstacleFt: 1470 },
    ] },
    { pressureAltFt: 4000, cells: [
      { oatC:  0, groundRollFt:  655, obstacleFt: 1430 },
      { oatC: 20, groundRollFt:  695, obstacleFt: 1490 },
      { oatC: 40, groundRollFt:  740, obstacleFt: 1550 },
    ] },
    { pressureAltFt: 6000, cells: [
      { oatC:  0, groundRollFt:  705, obstacleFt: 1510 },
      { oatC: 20, groundRollFt:  750, obstacleFt: 1570 },
      { oatC: 40, groundRollFt:  800, obstacleFt: 1635 },
    ] },
    { pressureAltFt: 8000, cells: [
      { oatC:  0, groundRollFt:  765, obstacleFt: 1590 },
      { oatC: 20, groundRollFt:  810, obstacleFt: 1655 },
      { oatC: 40, groundRollFt:  865, obstacleFt: 1725 },
    ] },
  ],
  perKtHeadwind: 0.011,
  perKtTailwind: 0.05,
  surfaceMultipliers: { paved: 1.00, dryGrass: 1.40, wetGrass: 1.50, wetPaved: 1.00 },
  source: 'POH PA-28-181 §5 — verify against your aircraft',
};

// ─── Cirrus SR22 (G3+ shapes; MGW 3400 lb) ─────────────────────────────
// Conservative POH §5 figures. SR22 numbers shorten markedly with
// headwind so the wind correction factor is slightly larger than Cessnas.

export const SR22_TAKEOFF: PerformanceDistanceTable = {
  weightLb: 3400,
  flaps: '50%',
  rows: [
    { pressureAltFt: 0,    cells: [
      { oatC:  0, groundRollFt: 1010, obstacleFt: 1655 },
      { oatC: 20, groundRollFt: 1180, obstacleFt: 1925 },
      { oatC: 40, groundRollFt: 1370, obstacleFt: 2230 },
    ] },
    { pressureAltFt: 2000, cells: [
      { oatC:  0, groundRollFt: 1180, obstacleFt: 1920 },
      { oatC: 20, groundRollFt: 1380, obstacleFt: 2240 },
      { oatC: 40, groundRollFt: 1605, obstacleFt: 2605 },
    ] },
    { pressureAltFt: 4000, cells: [
      { oatC:  0, groundRollFt: 1385, obstacleFt: 2245 },
      { oatC: 20, groundRollFt: 1625, obstacleFt: 2625 },
      { oatC: 40, groundRollFt: 1895, obstacleFt: 3060 },
    ] },
    { pressureAltFt: 6000, cells: [
      { oatC:  0, groundRollFt: 1635, obstacleFt: 2640 },
      { oatC: 20, groundRollFt: 1925, obstacleFt: 3095 },
      { oatC: 40, groundRollFt: 2250, obstacleFt: 3615 },
    ] },
    { pressureAltFt: 8000, cells: [
      { oatC:  0, groundRollFt: 1945, obstacleFt: 3130 },
      { oatC: 20, groundRollFt: 2295, obstacleFt: 3680 },
      { oatC: 40, groundRollFt: 2690, obstacleFt: 4310 },
    ] },
  ],
  perKtHeadwind: 0.0125,
  perKtTailwind: 0.05,
  surfaceMultipliers: { paved: 1.00, dryGrass: 1.15, wetGrass: 1.30, wetPaved: 1.00 },
  source: 'POH SR22 §5 — verify against your aircraft & avionics block',
};

export const SR22_LANDING: PerformanceDistanceTable = {
  weightLb: 3400,
  flaps: '100%',
  rows: [
    { pressureAltFt: 0,    cells: [
      { oatC:  0, groundRollFt: 1115, obstacleFt: 2230 },
      { oatC: 20, groundRollFt: 1175, obstacleFt: 2310 },
      { oatC: 40, groundRollFt: 1235, obstacleFt: 2390 },
    ] },
    { pressureAltFt: 2000, cells: [
      { oatC:  0, groundRollFt: 1195, obstacleFt: 2350 },
      { oatC: 20, groundRollFt: 1255, obstacleFt: 2435 },
      { oatC: 40, groundRollFt: 1320, obstacleFt: 2520 },
    ] },
    { pressureAltFt: 4000, cells: [
      { oatC:  0, groundRollFt: 1280, obstacleFt: 2475 },
      { oatC: 20, groundRollFt: 1345, obstacleFt: 2565 },
      { oatC: 40, groundRollFt: 1415, obstacleFt: 2660 },
    ] },
    { pressureAltFt: 6000, cells: [
      { oatC:  0, groundRollFt: 1370, obstacleFt: 2610 },
      { oatC: 20, groundRollFt: 1440, obstacleFt: 2705 },
      { oatC: 40, groundRollFt: 1515, obstacleFt: 2805 },
    ] },
    { pressureAltFt: 8000, cells: [
      { oatC:  0, groundRollFt: 1465, obstacleFt: 2755 },
      { oatC: 20, groundRollFt: 1540, obstacleFt: 2855 },
      { oatC: 40, groundRollFt: 1625, obstacleFt: 2960 },
    ] },
  ],
  perKtHeadwind: 0.0125,
  perKtTailwind: 0.05,
  surfaceMultipliers: { paved: 1.00, dryGrass: 1.45, wetGrass: 1.55, wetPaved: 1.00 },
  source: 'POH SR22 §5 — verify against your aircraft & avionics block',
};

// ─── Cirrus SR20 ───────────────────────────────────────────────────────
// Mirror of SR22 with slightly shorter ground rolls (lower power loading).

export const SR20_TAKEOFF: PerformanceDistanceTable = {
  weightLb: 3050,
  flaps: '50%',
  rows: [
    { pressureAltFt: 0,    cells: [
      { oatC:  0, groundRollFt: 1090, obstacleFt: 1840 },
      { oatC: 20, groundRollFt: 1280, obstacleFt: 2155 },
      { oatC: 40, groundRollFt: 1495, obstacleFt: 2500 },
    ] },
    { pressureAltFt: 4000, cells: [
      { oatC:  0, groundRollFt: 1525, obstacleFt: 2545 },
      { oatC: 20, groundRollFt: 1790, obstacleFt: 2975 },
      { oatC: 40, groundRollFt: 2090, obstacleFt: 3460 },
    ] },
    { pressureAltFt: 8000, cells: [
      { oatC:  0, groundRollFt: 2155, obstacleFt: 3560 },
      { oatC: 20, groundRollFt: 2540, obstacleFt: 4170 },
      { oatC: 40, groundRollFt: 2980, obstacleFt: 4870 },
    ] },
  ],
  perKtHeadwind: 0.0125,
  perKtTailwind: 0.05,
  surfaceMultipliers: { paved: 1.00, dryGrass: 1.15, wetGrass: 1.30, wetPaved: 1.00 },
  source: 'POH SR20 §5 — verify against your aircraft',
};

export const SR20_LANDING: PerformanceDistanceTable = {
  weightLb: 3050,
  flaps: '100%',
  rows: [
    { pressureAltFt: 0,    cells: [
      { oatC:  0, groundRollFt: 1080, obstacleFt: 2170 },
      { oatC: 20, groundRollFt: 1140, obstacleFt: 2250 },
      { oatC: 40, groundRollFt: 1200, obstacleFt: 2330 },
    ] },
    { pressureAltFt: 4000, cells: [
      { oatC:  0, groundRollFt: 1240, obstacleFt: 2410 },
      { oatC: 20, groundRollFt: 1305, obstacleFt: 2500 },
      { oatC: 40, groundRollFt: 1375, obstacleFt: 2590 },
    ] },
    { pressureAltFt: 8000, cells: [
      { oatC:  0, groundRollFt: 1420, obstacleFt: 2680 },
      { oatC: 20, groundRollFt: 1495, obstacleFt: 2780 },
      { oatC: 40, groundRollFt: 1575, obstacleFt: 2880 },
    ] },
  ],
  perKtHeadwind: 0.0125,
  perKtTailwind: 0.05,
  surfaceMultipliers: { paved: 1.00, dryGrass: 1.45, wetGrass: 1.55, wetPaved: 1.00 },
  source: 'POH SR20 §5 — verify against your aircraft',
};

// ─── Cessna 172N (older flap-40 model) ─────────────────────────────────

export const C172N_TAKEOFF: PerformanceDistanceTable = {
  weightLb: 2300,
  flaps: '10°',
  rows: [
    { pressureAltFt: 0,    cells: [
      { oatC:  0, groundRollFt:  775, obstacleFt: 1390 },
      { oatC: 20, groundRollFt:  890, obstacleFt: 1595 },
      { oatC: 40, groundRollFt: 1020, obstacleFt: 1825 },
    ] },
    { pressureAltFt: 4000, cells: [
      { oatC:  0, groundRollFt: 1090, obstacleFt: 1960 },
      { oatC: 20, groundRollFt: 1255, obstacleFt: 2245 },
      { oatC: 40, groundRollFt: 1440, obstacleFt: 2570 },
    ] },
    { pressureAltFt: 8000, cells: [
      { oatC:  0, groundRollFt: 1565, obstacleFt: 2845 },
      { oatC: 20, groundRollFt: 1805, obstacleFt: 3260 },
      { oatC: 40, groundRollFt: 2080, obstacleFt: 3735 },
    ] },
  ],
  perKtHeadwind: 0.0111,
  perKtTailwind: 0.05,
  surfaceMultipliers: { paved: 1.00, dryGrass: 1.15, wetGrass: 1.30, wetPaved: 1.00 },
  source: 'POH C172N §5 — verify against your aircraft',
};

export const C172N_LANDING: PerformanceDistanceTable = {
  weightLb: 2300,
  flaps: '40°',
  rows: [
    { pressureAltFt: 0,    cells: [
      { oatC:  0, groundRollFt:  500, obstacleFt: 1230 },
      { oatC: 20, groundRollFt:  535, obstacleFt: 1280 },
      { oatC: 40, groundRollFt:  570, obstacleFt: 1330 },
    ] },
    { pressureAltFt: 4000, cells: [
      { oatC:  0, groundRollFt:  580, obstacleFt: 1360 },
      { oatC: 20, groundRollFt:  620, obstacleFt: 1415 },
      { oatC: 40, groundRollFt:  660, obstacleFt: 1475 },
    ] },
    { pressureAltFt: 8000, cells: [
      { oatC:  0, groundRollFt:  675, obstacleFt: 1505 },
      { oatC: 20, groundRollFt:  720, obstacleFt: 1565 },
      { oatC: 40, groundRollFt:  770, obstacleFt: 1635 },
    ] },
  ],
  perKtHeadwind: 0.0111,
  perKtTailwind: 0.05,
  surfaceMultipliers: { paved: 1.00, dryGrass: 1.45, wetGrass: 1.55, wetPaved: 1.00 },
  source: 'POH C172N §5 — verify against your aircraft',
};

// ─── Beech Bonanza A36 (placeholder/conservative) ──────────────────────

export const A36_TAKEOFF: PerformanceDistanceTable = {
  weightLb: 3650,
  flaps: 'Approach',
  rows: [
    { pressureAltFt: 0,    cells: [
      { oatC:  0, groundRollFt: 1010, obstacleFt: 1810 },
      { oatC: 20, groundRollFt: 1190, obstacleFt: 2120 },
      { oatC: 40, groundRollFt: 1390, obstacleFt: 2470 },
    ] },
    { pressureAltFt: 4000, cells: [
      { oatC:  0, groundRollFt: 1395, obstacleFt: 2475 },
      { oatC: 20, groundRollFt: 1640, obstacleFt: 2900 },
      { oatC: 40, groundRollFt: 1920, obstacleFt: 3380 },
    ] },
    { pressureAltFt: 8000, cells: [
      { oatC:  0, groundRollFt: 1980, obstacleFt: 3475 },
      { oatC: 20, groundRollFt: 2330, obstacleFt: 4070 },
      { oatC: 40, groundRollFt: 2730, obstacleFt: 4745 },
    ] },
  ],
  perKtHeadwind: 0.0125,
  perKtTailwind: 0.05,
  surfaceMultipliers: { paved: 1.00, dryGrass: 1.15, wetGrass: 1.30, wetPaved: 1.00 },
  source: 'POH Beech A36 §5 — verify against your aircraft',
};

export const A36_LANDING: PerformanceDistanceTable = {
  weightLb: 3650,
  flaps: 'Full',
  rows: [
    { pressureAltFt: 0,    cells: [
      { oatC:  0, groundRollFt:  920, obstacleFt: 1925 },
      { oatC: 20, groundRollFt:  970, obstacleFt: 1995 },
      { oatC: 40, groundRollFt: 1020, obstacleFt: 2065 },
    ] },
    { pressureAltFt: 4000, cells: [
      { oatC:  0, groundRollFt: 1050, obstacleFt: 2130 },
      { oatC: 20, groundRollFt: 1105, obstacleFt: 2210 },
      { oatC: 40, groundRollFt: 1165, obstacleFt: 2290 },
    ] },
    { pressureAltFt: 8000, cells: [
      { oatC:  0, groundRollFt: 1195, obstacleFt: 2370 },
      { oatC: 20, groundRollFt: 1260, obstacleFt: 2460 },
      { oatC: 40, groundRollFt: 1330, obstacleFt: 2555 },
    ] },
  ],
  perKtHeadwind: 0.0125,
  perKtTailwind: 0.05,
  surfaceMultipliers: { paved: 1.00, dryGrass: 1.45, wetGrass: 1.55, wetPaved: 1.00 },
  source: 'POH Beech A36 §5 — verify against your aircraft',
};

// ─── Public registry ───────────────────────────────────────────────────

interface PerformanceTemplate {
  /** ICAO type designator the seed matches against. */
  icaoType: string;
  /** Friendly display name. */
  name: string;
  takeoff: PerformanceDistanceTable;
  landing: PerformanceDistanceTable;
}

export const PERFORMANCE_TEMPLATES: PerformanceTemplate[] = [
  { icaoType: 'C182', name: 'Cessna 182G Skylane (1964)',  takeoff: C182G_TAKEOFF,    landing: C182G_LANDING    },
  { icaoType: 'C172', name: 'Cessna 172S Skyhawk',  takeoff: C172S_TAKEOFF,    landing: C172S_LANDING    },
  { icaoType: 'C72R', name: 'Cessna 172N (older)',  takeoff: C172N_TAKEOFF,    landing: C172N_LANDING    },
  { icaoType: 'P28A', name: 'Piper PA-28-181',      takeoff: PA28_181_TAKEOFF, landing: PA28_181_LANDING },
  { icaoType: 'SR22', name: 'Cirrus SR22',          takeoff: SR22_TAKEOFF,     landing: SR22_LANDING     },
  { icaoType: 'SR20', name: 'Cirrus SR20',          takeoff: SR20_TAKEOFF,     landing: SR20_LANDING     },
  { icaoType: 'BE36', name: 'Beech A36 Bonanza',    takeoff: A36_TAKEOFF,      landing: A36_LANDING      },
];

/** Find a template by ICAO type. Returns null when no match. */
export function findPerformanceTemplate(
  icaoType: string | undefined | null,
): PerformanceTemplate | null {
  if (!icaoType) return null;
  const upper = icaoType.toUpperCase();
  return PERFORMANCE_TEMPLATES.find((t) => t.icaoType === upper) ?? null;
}
